import React from 'react';
import { CalculationResult } from '../utils/roiCalculations';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsSummaryProps {
  results: CalculationResult;
}
export function ResultsSummary({
  results
}: ResultsSummaryProps) {
  const roiClass = results.roi >= 0 ? 'text-green-600' : 'text-red-600';
  const opportunityCost = results.totalGrossProfit;
  // Format breakeven to say "Upon Launch" instead of just "Launch"
  const breakevenText = results.breakeven === 'Launch' ? 'Upon Launch' : results.breakeven;
  return <div className="space-y-6 mb-6" data-id="element-404">
      {/* Opportunity Cost Banner */}
      <div className="bg-amber/10 border-l-4 border-amber p-4 md:p-6 rounded-r-lg shadow-sm" data-id="element-405">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-406">
          <div data-id="element-407">
            <h3 className="text-lg md:text-xl font-bold text-navy mb-1" data-id="element-408">
              The Cost of Inaction
            </h3>
            <p className="text-sm md:text-base text-gray-700 max-w-2xl" data-id="element-409">
              By not implementing this recruiting system, you will be
              potentially missing out on an additional{' '}
              <span className="font-bold text-navy" data-id="element-410">
                {fmtMoney(opportunityCost)}
              </span>{' '}
              in gross profit over the next 12 months.
            </p>
          </div>
          <div className="text-2xl md:text-3xl font-bold text-amber whitespace-nowrap" data-id="element-411">
            {fmtMoney(opportunityCost)}/yr
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-412">
        {/* Hires / Month */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-413">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-414">
            Hiring Velocity
          </div>
          <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-415">
            {results.hiresPerMonth.toLocaleString()}
            <span className="text-lg text-gray-400 font-normal" data-id="element-416">/mo</span>
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-417">
            {results.launchedPerMonth.toFixed(1)} reps launched/mo
          </div>
        </div>

        {/* Total Investment */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-418">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-419">
            12-Mo Investment
          </div>
          <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-420">
            {fmtMoney(results.totalCostAll)}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-421">
            Includes ad spend & time cost
          </div>
        </div>

        {/* Gross Profit */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-422">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-423">
            12-Mo Gross Profit
          </div>
          <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-424">
            {fmtMoney(results.totalGrossProfit)}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-425">
            {fmtMoney(results.gpPerSale)} GP per sale
          </div>
        </div>

        {/* ROI */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-426">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-427">
            12-Month ROI
          </div>
          <div className={`text-2xl md:text-3xl font-bold ${roiClass}`} data-id="element-428">
            {fmtPct(results.roi)}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-429">
            Net Profit: {fmtMoney(results.totalNetProfit)}
          </div>
        </div>

        {/* Break-Even Point */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-430">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-431">
            Break-Even Point
          </div>
          <div className="text-2xl md:text-3xl font-bold text-amber" data-id="element-432">
            {breakevenText}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-433">
            When revenue exceeds investment
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-gray-500 italic text-center mt-4" data-id="element-434">
        *These projections are estimates based on the inputs provided and are
        not exact figures. Actual results may vary significantly based on market
        conditions, execution, and other factors. HIRE does not guarantee any
        specific outcomes or results.
      </p>
    </div>;
}